/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import '../styles/main.scss';

import { graphql } from 'gatsby';
import groq from 'groq';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ModulesContent, { CommonModuleProps, Module } from '../components/ModulesContent';
import PageSEO from '../components/PageSEO';
import Hero, { HeroType } from '../components/ui/Hero';
import HomepageHero from '../components/ui/HomepageHero';
import SanityLayout from '../components/ui/SanityLayout';
import { ThemeModuleProps } from '../components/ui/modules/ThemeModule';
import { ALL_LANGS } from '../constants';
import { LocalizedSEO } from '../graphql-fragments/seo';
import { withPagePreview } from '../preview/previewUtils';
import { withI18n } from '../utils/hocs';
import { useIntersectionObserver } from '../utils/hooks';
import { getPortableTextAsString, localize } from '../utils/sanity';
import {
  buildFAQPageSchema,
  buildVideoObjectSchema,
} from '../utils/structuredData';
import getYouTubeId from 'get-youtube-id';

export const query = graphql`
  query GetPageById($id: String!) {
    sanityPage(_id: { eq: $id }) {
      title {
        ...LocaleStringFragment
      }
      slug {
        current
      }
      hero {
        ...Hero
      }
      addCloudsToTheFront
      modules {
        ...Modules
      }
      seo {
        ...SEO
      }
    }
    allSanityExperience(sort: { orderRank: ASC }) {
      nodes {
        theme
      }
    }
  }
`;

const groqQuery = groq`{
  "sanityPage": *[_id == $id][0] {
     ...
  }
}`;

export type BackgroundColorTheme = 'panoramic' | 'evening' | 'sunset' | 'moonlit' | 'starlit';

interface ExperienceTheme {
  theme: BackgroundColorTheme;
}

interface LocalizedQueryData {
  sanityPage: {
    title: string;
    slug: {
      current: string;
    };
    hero?: HeroType & { disableHeroAnimation?: boolean };
    addCloudsToTheFront?: boolean;
    modules?: Array<Module>;
    seo?: LocalizedSEO;
  };
  allSanityExperience: {
    nodes: Array<ExperienceTheme>;
  };
}

/**
 * Determines the last theme-affecting module's theme.
 * Works backwards through modules to find either a ThemeModule or ExperiencesBookingModule.
 */
function getLastModuleTheme(
  modules: Module[],
  allExperiences: ExperienceTheme[],
): BackgroundColorTheme | undefined {
  for (let i = modules.length - 1; i >= 0; i--) {
    const module = modules[i];

    if (module.__typename === 'SanityThemeModule') {
      return module.theme;
    }

    if (module.__typename === 'SanityExperiencesBookingModule') {
      if (module.experiencesToUse === 'chooseManually' && module.chosenExperiences?.length) {
        return module.chosenExperiences[module.chosenExperiences.length - 1].theme;
      } else {
        // 'all' mode - use last experience from full list
        return allExperiences[allExperiences.length - 1]?.theme;
      }
    }
  }
  return undefined;
}

const Page = withPagePreview(
  {
    groqQuery,
  },
  ({ data }): React.ReactElement => {
    const { i18n } = useTranslation();

    const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
    const localizedData: LocalizedQueryData = localize(data, langs);
    const { slug, title, hero, addCloudsToTheFront, modules, seo } = localizedData.sanityPage;
    const allExperiences = localizedData.allSanityExperience.nodes;

    function isThemeModule(
      module: Module,
    ): module is ThemeModuleProps & CommonModuleProps & { __typename: 'SanityThemeModule' } {
      return module.__typename === 'SanityThemeModule';
    }

    const lastThemeOfThePage =
      modules && modules.length > 0
        ? getLastModuleTheme(modules, allExperiences)
        : undefined;

    const pageTheme =
      (modules &&
        modules.length > 0 &&
        isThemeModule(modules[0]) &&
        modules[0].theme) ||
      'panoramic';

    const structuredData: Record<string, unknown>[] = [];
    if (modules) {
      const faqItems = modules.flatMap(m => {
        if (m.__typename !== 'SanityAccordionModule' || !m.isFAQ || m.disabled) return [];
        return m.accordions.map(entry => ({
          question: entry.title,
          answer: getPortableTextAsString(entry.text),
        }));
      });
      const faqSchema = buildFAQPageSchema(faqItems);
      if (faqSchema) structuredData.push(faqSchema);

      modules.forEach(m => {
        if (m.__typename !== 'SanityVideoModule' || m.disabled) return;
        const videoId = m.youtubeUrl ? getYouTubeId(m.youtubeUrl) : null;
        const thumbnailUrl =
          m.videoThumbnailUrl ||
          (videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : undefined);
        const videoSchema = buildVideoObjectSchema({
          name: m.videoTitle || '',
          description: m.videoDescription,
          thumbnailUrl,
          uploadDate: m.videoUploadDate,
          embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : undefined,
          contentUrl: m.youtubeUrl,
        });
        if (videoSchema) structuredData.push(videoSchema);
      });
    }

    const [homepageHeroRef, homepageHeroIntersectionObserverEntry] = useIntersectionObserver({
      threshold: 0.5,
    });

    return (
      <SanityLayout
        pageTheme={pageTheme}
        lastModuleTheme={lastThemeOfThePage || pageTheme}
        frontCloudPosition={addCloudsToTheFront ? 'fullScreen' : undefined}
        hideCookieBar={
          (homepageHeroIntersectionObserverEntry?.intersectionRatio ??
            (slug.current === '/' ? 1 : 0)) > 0.5
        }
      >
        <PageSEO
          defaultTitle={slug.current === '/' ? '' : title}
          pageSEO={seo}
          structuredData={structuredData.length > 0 ? structuredData : undefined}
        />
        {slug.current === '/' ? (
          <div ref={homepageHeroRef}>
            <HomepageHero title={hero?.title || title} disableAnimation={hero?.disableHeroAnimation} />
          </div>
        ) : (
          <Hero {...hero} title={hero?.title || title} />
        )}
        {modules && modules.length > 0 && (
          <ModulesContent modules={modules} pageTheme={pageTheme || 'panoramic'} />
        )}
      </SanityLayout>
    );
  },
);

export default withI18n(Page);
